import {
  Badge,
  Button,
  ButtonList,
  DataTable,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Page,
  SearchInput,
  Skeleton,
  SortStatus,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { SortDirection } from 'lib-shared-types-api';
import type {
  QueueWithAssignmentCounts,
  ReadQueuesSortKey,
} from 'lib-shared-types-api';
import { CSSProperties, useCallback, useMemo, useState } from 'react';

import { queuesApi } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

export const QueuesTable = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const hasQueueWriteAccess = useHasAccess('settings.queue:w', {
    fallbackRole: 'admin',
  });
  const hasQueueEditAccess = useHasAccess(
    ['settings.queue.offering', 'settings.queue.members'],
    { strategy: 'any' },
  );

  const { teamsAssigningTeamsToQueues } = useFeatureFlag([
    'teams-assigning-teams-to-queues',
  ]);

  const [sortStatus, setSortStatus] = useState<SortStatus | null>({
    sortBy: 'name',
    direction: 'asc',
  });

  const {
    arg,
    items: queues,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    changeArg,
    nextPage,
    previousPage,
    refetchItems,
    search,
    updateItem,
    removeItem,
  } = usePaginatedEndpoint({
    endpoint: queuesApi.endpoints.readQueues,
    itemsSelector: ({ data: { queues } }) => queues,
    initialArg: {
      isArchived: false,
      sort: { key: 'name', direction: 'asc' },
    },
  });

  const isSuccess = asyncStatus === 'fulfilled';
  const isError = asyncStatus === 'rejected';
  const isLoading = asyncStatus === 'pending';

  const filters = useMemo(
    () => [
      {
        key: 'all',
        label: 'All queues',
        isSelected: arg?.isArchived === undefined,
        onClick: () => changeArg({ isArchived: undefined }),
      },
      {
        key: 'active',
        label: 'Active queues',
        isSelected: arg?.isArchived === false,
        onClick: () => changeArg({ isArchived: false }),
      },
      {
        key: 'archived',
        label: 'Archived queues',
        isSelected: arg?.isArchived === true,
        onClick: () => changeArg({ isArchived: true }),
      },
    ],
    [arg?.isArchived, changeArg],
  );

  const selectedFilter = useMemo(
    () => filters.find(({ isSelected }) => isSelected),
    [filters],
  );

  const handleSearchToggle = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && arg?.searchTerm) {
        changeArg({
          searchTerm: '',
        });
      }
    },
    [arg, changeArg],
  );

  const handleSearchSubmit = useCallback(
    (value: string) => {
      search(value, { debounceTime: 300 });
    },
    [search],
  );

  const handleChangeSort = useCallback(
    (status: SortStatus | null) => {
      setSortStatus(status);

      changeArg({
        sort: status
          ? {
              key: status.sortBy as ReadQueuesSortKey,
              direction: status.direction as SortDirection,
            }
          : {
              key: 'name',
              direction: 'asc',
            },
      });
    },
    [changeArg],
  );

  const handleGoToCreatePage = useCallback(() => {
    navigate({ path: '/settings/queues/create' });
    ampli.settingsQueuesAddQueueClicked();
  }, [navigate]);

  const handleGoToEditPage = useCallback(
    (queue: QueueWithAssignmentCounts) => {
      navigate({
        path: '/settings/queues/:id',
        params: { path: { id: queue.id } },
      });
      ampli.settingsQueuesEditQueueClicked();
    },
    [navigate],
  );

  const handleToggleArchive = useCallback(
    async (queue: QueueWithAssignmentCounts) => {
      await dispatch(
        appActions.showModal({
          modal: {
            type: 'settings:queueArchive',
            arg: {
              queue,
              goToArchivedQueues: () => changeArg({ isArchived: true }),
            },
          },
        }),
      );

      if (selectedFilter?.key === 'all') {
        updateItem(
          { ...queue, is_archived: !queue.is_archived },
          (a, b) => a.id === b.id,
        );
      } else {
        removeItem(queue, (a, b) => a.id === b.id);
      }
    },
    [changeArg, dispatch, removeItem, updateItem, selectedFilter],
  );

  const columnStyle: CSSProperties = {
    width: '15%',
  };

  return (
    <Page.Section
      title="All queues"
      controls={
        <>
          <SearchInput
            onToggle={handleSearchToggle}
            onSubmit={handleSearchSubmit}
          />

          <DropdownButton
            dropdown={
              <DropdownButtonContext.Consumer>
                {({ toggleDropdown }) => (
                  <ButtonList>
                    {filters.map(({ label, isSelected, onClick }) => (
                      <ButtonList.Item
                        key={label}
                        isSelected={isSelected}
                        onClick={() => {
                          toggleDropdown();
                          onClick();
                        }}
                      >
                        {label}
                      </ButtonList.Item>
                    ))}
                  </ButtonList>
                )}
              </DropdownButtonContext.Consumer>
            }
          >
            {selectedFilter?.label ?? 'Filter'}
          </DropdownButton>

          <Tooltip
            content="You don't have permissions to add queues"
            disabled={hasQueueWriteAccess}
            dark
          >
            <Button
              onClick={handleGoToCreatePage}
              disabled={!hasQueueWriteAccess}
              id="add-queue-btn"
            >
              Add queue
            </Button>
          </Tooltip>
        </>
      }
    >
      <DataTable
        columns={[
          {
            label: 'Name',
            columnId: 'name',
            sortable: true,
            selector: (queue) => {
              if (isLoading) {
                return <Skeleton height="14px" width="40%" />;
              }

              return (
                <FlexBox gap="250" alignItems="center">
                  {hasQueueEditAccess ? (
                    <AppLink
                      path="/settings/queues/:id"
                      params={{
                        path: {
                          id: queue.id,
                        },
                      }}
                    >
                      <Text.Paragraph as="span">{queue.name}</Text.Paragraph>
                    </AppLink>
                  ) : (
                    <Text.Paragraph as="span">{queue.name}</Text.Paragraph>
                  )}

                  {queue.is_default && (
                    <Badge variant="primary" size="small">
                      Default
                    </Badge>
                  )}

                  {queue.is_archived && (
                    <Badge variant="error" size="small">
                      Archived
                    </Badge>
                  )}
                </FlexBox>
              );
            },
          },
          {
            label: 'Priority',
            columnId: 'priority',
            sortable: true,
            selector: (queue) => {
              if (isLoading) {
                return <Skeleton height="14px" width="20px" />;
              }
              return queue.priority;
            },
            style: columnStyle,
          },
          ...(teamsAssigningTeamsToQueues
            ? [
                {
                  label: 'Teams',
                  selector: (queue) => {
                    if (isLoading) {
                      return <Skeleton height="14px" width="20px" />;
                    }
                    return queue.number_of_teams;
                  },
                  style: columnStyle,
                },
              ]
            : []),
          {
            label: 'Agents',
            selector: (queue) => {
              if (isLoading) {
                return <Skeleton height="14px" width="20px" />;
              }
              return queue.number_of_agents;
            },
            style: columnStyle,
          },
          {
            label: '',
            selector: (queue) => {
              if (isLoading) {
                return (
                  <FlexBox
                    justifyContent="center"
                    alignItems="center"
                    width="32px"
                    height="32px"
                  >
                    <Skeleton height="18px" width="18px" />
                  </FlexBox>
                );
              }

              return (
                <DropdownButton
                  icon="dots-h"
                  buttonProps={{ variant: 'tertiary' }}
                  noArrow
                  fixed
                  dropdown={
                    <DropdownButtonContext.Consumer>
                      {({ toggleDropdown }) => (
                        <ButtonList>
                          <Tooltip
                            content="You don't have permissions to edit queues"
                            disabled={hasQueueEditAccess}
                            dark
                          >
                            <ButtonList.Item
                              isDisabled={!hasQueueEditAccess}
                              onClick={() => handleGoToEditPage(queue)}
                            >
                              Edit
                            </ButtonList.Item>
                          </Tooltip>

                          <Tooltip
                            content={
                              queue.is_default
                                ? 'The default queue cannot be archived'
                                : "You don't have permissions to archive queues"
                            }
                            disabled={!queue.is_default && hasQueueWriteAccess}
                            dark
                          >
                            <ButtonList.Item
                              isDisabled={
                                queue.is_default || !hasQueueWriteAccess
                              }
                              onClick={() => {
                                toggleDropdown();
                                handleToggleArchive(queue);
                              }}
                            >
                              {queue.is_archived ? 'Unarchive' : 'Archive'}
                            </ButtonList.Item>
                          </Tooltip>
                        </ButtonList>
                      )}
                    </DropdownButtonContext.Consumer>
                  }
                />
              );
            },
            style: {
              width: 0,
            },
          },
        ]}
        data={
          asyncStatus === 'pending'
            ? (Array.from({ length: 25 }) as typeof queues)
            : queues
        }
        paginationProps={{
          previousButtonDisabled: !hasPreviousPage || !isSuccess,
          previousButtonClick: previousPage,
          nextButtonDisabled: !hasNextPage || !isSuccess,
          nextButtonClick: nextPage,
        }}
        sortingProps={{
          sortStatus,
          onSortChange: handleChangeSort,
        }}
        tableStatusProps={{
          loadingFailed: isError,
          onLoadingRetry: refetchItems,
          emptyState:
            !queues.length && isSuccess
              ? {
                  title: 'No queues found',
                  description: 'Try changing your filters or search criteria.',
                }
              : undefined,
        }}
        tableStyleProps={{
          stickyHeader: true,
          stickyFooter: true,
        }}
      />
    </Page.Section>
  );
};
